"use client"

import { useState, type CSSProperties } from "react"
import { ChevronDown } from "lucide-react"
import { CareersHeader } from "@/components/max/CareersHeader"
import { CareersFooter } from "@/components/max/CareersFooter"
import { CAREERS_MAX_W, CAREERS_INSET } from "@/lib/career/layout"
import { FAQ_SECTIONS, type QA } from "./faqData"

// careers.signalmax.ai/faq is the PUBLIC FAQ (middleware rewrites /faq -> /max/faq).
// Same shell as the landing (CareerLogo header, smx tokens, footer). Answers are
// grounded in what the product actually does; no fabricated numbers, no
// "guaranteed internship". Copy is dash-free per house style.

const INSET = CAREERS_INSET
const shell: CSSProperties = { maxWidth: CAREERS_MAX_W, margin: "0 auto" }

function FaqItem({ id, item, open, onToggle }: { id: string; item: QA; open: boolean; onToggle: (id: string) => void }) {
  // Differentiation-pass items get an info-tinted border + a "New" badge (per the handoff).
  const border = item.isNew ? "color-mix(in srgb, var(--status-info-border) 55%, var(--border-subtle))" : "var(--border-subtle)"
  return (
    <div style={{ border: `1px solid ${border}`, borderRadius: "var(--radius-lg)", background: "var(--surface)", overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => onToggle(id)}
        aria-expanded={open}
        style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, minHeight: 56, padding: "16px 18px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left", color: "inherit" }}
      >
        <span style={{ flex: 1, minWidth: 0, fontSize: "var(--type-base)", fontWeight: 600, color: "var(--text-primary)" }}>{item.q}</span>
        {item.isNew && <span className="smx-badge smx-soft t-info" style={{ flexShrink: 0, fontSize: 10, letterSpacing: "0.04em" }}>New</span>}
        <ChevronDown className="size-5" style={{ flexShrink: 0, color: "var(--text-muted)", transition: "transform .18s ease", transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && (
        <div style={{ padding: "0 18px 18px", fontSize: "var(--type-sm)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)", maxWidth: "68ch" }}>
          {item.a}
          {item.href && (
            <>{" "}<a href={item.href} style={{ color: "var(--accent-text)", fontWeight: 600, textDecoration: "none" }}>{item.linkLabel || "Learn more"} →</a></>
          )}
        </div>
      )}
    </div>
  )
}

export default function FaqClient() {
  const [open, setOpen] = useState<Set<string>>(new Set())
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="theme-max smx" style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text-primary)", fontSize: "var(--type-base)" }}>
      <CareersHeader />

      <main style={{ ...shell, padding: `clamp(36px, 6vw, 72px) ${INSET} clamp(40px, 6vw, 72px)` }}>
        <div style={{ marginBottom: "clamp(24px, 4vw, 40px)" }}>
          <div style={{ fontSize: "var(--type-2xs)", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--accent-text)", marginBottom: 8 }}>FAQ</div>
          <h1 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 48px)", lineHeight: 1.05, letterSpacing: "-0.03em" }}>Questions, answered.</h1>
          <p style={{ margin: "10px 0 0", maxWidth: "52ch", fontSize: "var(--type-base)", lineHeight: "var(--leading-relaxed)", color: "var(--text-secondary)" }}>What SignalMax Careers is, how it differs from the boards you already use, how it decides what you qualify for, and what it will not promise you.</p>
        </div>

        {/* Full-width responsive section grid: a single skinny column read as "narrow" on a laptop,
            so sections now flow into as many readable columns as the width allows. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: "clamp(24px, 3vw, 40px)", alignItems: "start" }}>
          {FAQ_SECTIONS.map((section) => (
            <section key={section.title}>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "var(--type-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 12 }}>{section.title}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {section.items.map((item, i) => {
                  const id = `${section.title}-${i}`
                  return <FaqItem key={id} id={id} item={item} open={open.has(id)} onToggle={toggle} />
                })}
              </div>
            </section>
          ))}
        </div>

        <div style={{ marginTop: "clamp(32px, 5vw, 56px)", padding: "24px 22px", borderRadius: "var(--radius-xl)", border: "1px solid var(--accent)", background: "linear-gradient(120deg, color-mix(in srgb, var(--accent) 12%, var(--surface)), var(--surface) 60%)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "var(--type-lg)", fontWeight: 700 }}>Still have a question?</div>
            <div style={{ fontSize: "var(--type-sm)", color: "var(--text-secondary)", marginTop: 4 }}>Try it free first. No account or card needed to see your matches.</div>
          </div>
          <a href="/try" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 46, padding: "0 22px", fontSize: 15, fontWeight: 700, borderRadius: "var(--radius-md)", background: "var(--accent)", color: "var(--on-accent, #fff)", textDecoration: "none", whiteSpace: "nowrap" }}>Find my internships</a>
        </div>
      </main>

      <CareersFooter />
    </div>
  )
}
